import React, { useCallback, useRef, useEffect } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { LineAnnotation } from './annotations/LineAnnotation';
import { RectangleAnnotation } from './annotations/RectangleAnnotation';
import { CircleAnnotation } from './annotations/CircleAnnotation';
import { TextAnnotation } from './annotations/TextAnnotation';
import { ImageAnnotation } from './annotations/ImageAnnotation';
import { ArrowAnnotation } from './annotations/ArrowAnnotation';
import { MeasurementAnnotation } from './annotations/MeasurementAnnotation';
import { Annotation } from '@/services/annotationService';
import { logger } from '@/lib/logger';
import { useAnnotations } from '@/hooks/useAnnotations';
import { moveAnnotationToAnchor } from '@/utils/annotationPosition';

interface AnnotationLayerProps {
  scale: number;
  onStartDrag?: (
    type: 'annotation',
    id: string,
    e: React.MouseEvent | React.TouchEvent,
    elementPos?: { x: number; y: number },
  ) => void;
  isDragging: boolean;
  dragState: {
    type: 'fixture' | 'control' | 'panel' | 'room' | 'product' | 'annotation';
    id: string;
    startPos: { x: number; y: number };
    currentPos: { x: number; y: number };
    hasMovedThreshold: boolean;
    initialElementPos?: { x: number; y: number };
    clickOffset?: { x: number; y: number };
  } | null;
  wasRecentlyDragged?: (id: string) => boolean;
  isPublicView?: boolean;
}

export function AnnotationLayer({
  scale,
  onStartDrag,
  isDragging,
  dragState,
  wasRecentlyDragged,
  isPublicView = false,
}: AnnotationLayerProps) {
  // Get floor plan data
  const { data, floorPlan } = useFloorPlanContext();
  const floorPlanId = floorPlan.id;

  // Get annotations using the hook
  const { annotations, isLoading, error, deleteAnnotation, updateAnnotation, createAnnotation } =
    useAnnotations(floorPlanId);

  // Get store state and actions
  const showAnnotations = useAppStore(state => state.annotations.showAnnotations);
  const selectedAnnotations = useAppStore(state => state.selection.annotations);
  const toggleEntitySelection = useAppStore(state => state.toggleEntitySelection);
  const selectEntity = useAppStore(state => state.selectEntity);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const hoveredEntityId = useAppStore(state => state.ui.hoveredEntityId);
  const hoveredEntityType = useAppStore(state => state.ui.hoveredEntityType);
  const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const setActiveAnnotationType = useAppStore(state => state.setActiveAnnotationType);
  const setActiveMode = useAppStore(state => state.setActiveMode);

  // Get multi-selection state
  const selectedFixtures = useAppStore(state => state.selection.fixtures);
  const selectedControls = useAppStore(state => state.selection.controls);
  const selectedProducts = useAppStore(state => state.selection.products);
  const selectedRooms = useAppStore(state => state.selection.rooms);
  const totalSelections =
    selectedFixtures.size +
    selectedControls.size +
    selectedProducts.size +
    selectedAnnotations.size +
    selectedRooms.size;
  const isMultiSelection = totalSelections > 1;

  // Handle annotation click
  const handleAnnotationClick = useCallback(
    (e: React.MouseEvent, annotation: Annotation) => {
      if (isPublicView || isDragging) return;

      e.stopPropagation();

      logger.log('Annotation clicked:', {
        id: annotation.id,
        type: annotation.type,
        shiftKey: e.shiftKey,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        isMultiSelection,
        selectedAnnotations: Array.from(selectedAnnotations),
      });

      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        toggleEntitySelection('annotation', annotation.id);
        // For annotations, we don't open the multi-selection drawer
        // The context toolbar will appear automatically
      } else if (isMultiSelection && selectedAnnotations.has(annotation.id)) {
        // Already selected in multi-selection, just toggle it
        toggleEntitySelection('annotation', annotation.id);
      } else {
        // Single select
        selectEntity('annotation', annotation.id);

        // Clear any active placement tools when clicking an element
        setActiveAnnotationType(null);
        setActiveMode('default');

        // For single annotation, we don't need a drawer either
        // User can interact directly with the annotation
      }
    },
    [
      isPublicView,
      isDragging,
      toggleEntitySelection,
      selectEntity,
      selectedAnnotations,
      isMultiSelection,
      setActiveAnnotationType,
      setActiveMode,
    ],
  );

  // Handle mouse enter/leave for hover effects
  const handleMouseEnter = useCallback(
    (id: string) => {
      if (!isPublicView && !isDragging) {
        setHoveredEntity(id, 'annotation');
      }
    },
    [isPublicView, isDragging, setHoveredEntity],
  );

  const handleMouseLeave = useCallback(() => {
    if (!isPublicView && !isDragging) {
      setHoveredEntity(null, null);
    }
  }, [isPublicView, isDragging, setHoveredEntity]);

  // Handle keyboard events for deletion, rotation, and duplication
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Only handle if annotations are selected and we're not in public view
      if (selectedAnnotations.size === 0 || isPublicView) return;

      // Delete key or Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();

        // Delete all selected annotations
        const annotationIds = Array.from(selectedAnnotations);
        for (const id of annotationIds) {
          await deleteAnnotation(id);
        }

        // Clear selection
        clearEntitySelection('annotation');
      }

      // Duplicate key (Cmd+D or Ctrl+D)
      else if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();

        const annotationIds = Array.from(selectedAnnotations);
        const newAnnotationIds: string[] = [];

        for (const id of annotationIds) {
          const annotation = annotations.find(a => a.id === id);
          if (!annotation) continue;

          // Create a copy with slight offset
          const offset = 20;
          const newData = { ...annotation.data };

          // Adjust position based on type
          switch (annotation.type) {
            case 'rectangle':
            case 'image':
            case 'text':
              newData.x = newData.x! + offset;
              newData.y = newData.y! + offset;
              break;

            case 'circle':
              newData.cx = newData.cx! + offset;
              newData.cy = newData.cy! + offset;
              break;

            case 'line':
            case 'arrow':
            case 'measurement':
              newData.start = {
                x: newData.start!.x + offset,
                y: newData.start!.y + offset,
              };
              newData.end = {
                x: newData.end!.x + offset,
                y: newData.end!.y + offset,
              };
              break;
          }

          const newAnnotation = await createAnnotation({
            floor_plan_id: floorPlanId,
            type: annotation.type,
            data: newData,
            style: annotation.style,
          });

          if (newAnnotation) {
            newAnnotationIds.push(newAnnotation.id);
          }
        }

        // Select the new annotations
        if (newAnnotationIds.length > 0) {
          clearEntitySelection('annotation');
          for (const id of newAnnotationIds) {
            toggleEntitySelection('annotation', id);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedAnnotations,
    isPublicView,
    deleteAnnotation,
    clearEntitySelection,
    annotations,
    updateAnnotation,
    createAnnotation,
    floorPlanId,
    toggleEntitySelection,
  ]);

  // Touch event handlers for press-and-hold drag
  const touchTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const touchStartPositions = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Handle annotation drag
  const handleStartDrag = useCallback(
    (e: React.MouseEvent, annotation: Annotation) => {
      if (!onStartDrag || isPublicView) return;

      // Only start drag on left mouse button
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();

      // Get current tool from store
      const activeTool = useAppStore.getState().ui.toolbar.activeTool;

      // Only allow dragging when select tool is active
      if (activeTool === 'select') {
        // Get the annotation's current position based on its type
        const elementPos = { x: 0, y: 0 };

        switch (annotation.type) {
          case 'line':
          case 'arrow':
          case 'measurement':
            // For lines, arrows, and measurements, use the midpoint as the anchor
            elementPos.x = (annotation.data.start!.x + annotation.data.end!.x) / 2;
            elementPos.y = (annotation.data.start!.y + annotation.data.end!.y) / 2;
            break;
          case 'rectangle':
          case 'text':
          case 'image':
            // For rectangles, text, and images, use the top-left corner
            elementPos.x = annotation.data.x!;
            elementPos.y = annotation.data.y!;
            break;
          case 'circle':
            // For circles, use the center
            elementPos.x = annotation.data.cx!;
            elementPos.y = annotation.data.cy!;
            break;
        }

        onStartDrag('annotation', annotation.id, e, elementPos);
      }
    },
    [onStartDrag, isPublicView],
  );

  // Handle touch start for press-and-hold
  const handleTouchStart = useCallback(
    (annotation: Annotation, e: React.TouchEvent) => {
      // Only handle single touch
      if (e.touches.length !== 1 || isPublicView || !onStartDrag) return;

      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      const startPos = { x: touch.clientX, y: touch.clientY };
      touchStartPositions.current.set(annotation.id, startPos);

      // Set up press-and-hold timer (500ms)
      const timer = setTimeout(() => {
        // Trigger haptic feedback if available
        if ('vibrate' in navigator) {
          navigator.vibrate(10);
        }

        // Get annotation position
        const elementPos = { x: 0, y: 0 };

        switch (annotation.type) {
          case 'line':
          case 'arrow':
          case 'measurement':
            elementPos.x = (annotation.data.start!.x + annotation.data.end!.x) / 2;
            elementPos.y = (annotation.data.start!.y + annotation.data.end!.y) / 2;
            break;
          case 'rectangle':
          case 'text':
          case 'image':
            elementPos.x = annotation.data.x!;
            elementPos.y = annotation.data.y!;
            break;
          case 'circle':
            elementPos.x = annotation.data.cx!;
            elementPos.y = annotation.data.cy!;
            break;
        }

        // Start drag using the same handler
        const syntheticEvent = {
          clientX: touch.clientX,
          clientY: touch.clientY,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as React.MouseEvent;

        onStartDrag('annotation', annotation.id, syntheticEvent, elementPos);
      }, 500); // 500ms hold time

      touchTimers.current.set(annotation.id, timer);
    },
    [onStartDrag, isPublicView],
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (annotation: Annotation, e: React.TouchEvent) => {
      e.preventDefault();

      // Clear the timer
      const timer = touchTimers.current.get(annotation.id);
      if (timer) {
        clearTimeout(timer);
        touchTimers.current.delete(annotation.id);
      }

      // Check if it was a tap (not moved much)
      const startPos = touchStartPositions.current.get(annotation.id);
      if (startPos && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        const distance = Math.sqrt(
          Math.pow(touch.clientX - startPos.x, 2) + Math.pow(touch.clientY - startPos.y, 2),
        );

        // If moved less than 10 pixels, treat as a tap
        if (distance < 10) {
          handleAnnotationClick(e as any, annotation);
        }
      }

      touchStartPositions.current.delete(annotation.id);
    },
    [handleAnnotationClick],
  );

  // Handle touch move (cancel timer if moved too much)
  const handleTouchMove = useCallback((annotation: Annotation, e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;

    const startPos = touchStartPositions.current.get(annotation.id);
    if (startPos) {
      const touch = e.touches[0];
      const distance = Math.sqrt(
        Math.pow(touch.clientX - startPos.x, 2) + Math.pow(touch.clientY - startPos.y, 2),
      );

      // If moved more than 10 pixels, cancel the timer
      if (distance > 10) {
        const timer = touchTimers.current.get(annotation.id);
        if (timer) {
          clearTimeout(timer);
          touchTimers.current.delete(annotation.id);
        }
      }
    }
  }, []);

  // Update annotation position if being dragged
  const updateAnnotationPosition = useCallback(
    (annotation: Annotation): Annotation => {
      if (!isDragging || dragState?.type !== 'annotation' || dragState.id !== annotation.id) {
        return annotation;
      }

      return moveAnnotationToAnchor(annotation, dragState.currentPos);
    },
    [isDragging, dragState],
  );

  // Don't render if annotations are hidden or still loading
  if (!showAnnotations || isLoading) {
    logger.log('AnnotationLayer not rendering:', { showAnnotations, isLoading });
    return null;
  }

  // Render error state
  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-red-500 text-sm">{error?.message || 'Failed to load annotations'}</div>
      </div>
    );
  }

  // Log for debugging
  if (annotations.length > 0) {
    logger.log('AnnotationLayer render:', {
      floorPlanId,
      annotationsCount: annotations.length,
      showAnnotations,
      imageSize: data.imageSize,
    });
  }

  return (
    <svg
      data-annotation-layer="true"
      className="pointer-events-none overflow-visible"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: data.imageSize?.width || 1920,
        height: data.imageSize?.height || 1080,
        zIndex: 30,
      }}
      viewBox={`0 0 ${data.imageSize?.width || 1920} ${data.imageSize?.height || 1080}`}
    >
      {annotations.map(annotation => {
        const isSelected = selectedAnnotations.has(annotation.id);
        const isHovered = hoveredEntityId === annotation.id && hoveredEntityType === 'annotation';
        const displayAnnotation = updateAnnotationPosition(annotation);

        // Common props for all annotation types
        const commonProps = {
          annotation: displayAnnotation,
          isSelected,
          isHovered,
          scale,
          onClick: (e: React.MouseEvent) => handleAnnotationClick(e, annotation),
          onMouseEnter: () => handleMouseEnter(annotation.id),
          onMouseLeave: handleMouseLeave,
          onMouseDown: (e: React.MouseEvent) => handleStartDrag(e, annotation),
          onTouchStart: (e: React.TouchEvent) => handleTouchStart(annotation, e),
          onTouchEnd: (e: React.TouchEvent) => handleTouchEnd(annotation, e),
          onTouchMove: (e: React.TouchEvent) => handleTouchMove(annotation, e),
          isInteractive: !isPublicView,
        };

        // Render the appropriate annotation component based on type
        switch (annotation.type) {
          case 'line':
            return <LineAnnotation key={annotation.id} {...commonProps} />;
          case 'rectangle':
            return <RectangleAnnotation key={annotation.id} {...commonProps} />;
          case 'circle':
            return <CircleAnnotation key={annotation.id} {...commonProps} />;
          case 'text':
            return <TextAnnotation key={annotation.id} {...commonProps} />;
          case 'image':
            return <ImageAnnotation key={annotation.id} {...commonProps} />;
          case 'arrow':
            return <ArrowAnnotation key={annotation.id} {...commonProps} />;
          case 'measurement':
            return (
              <MeasurementAnnotation
                key={annotation.id}
                {...commonProps}
                floorPlanId={floorPlanId}
              />
            );
          default:
            logger.warn(`Unknown annotation type: ${annotation.type}`);
            return null;
        }
      })}
    </svg>
  );
}
